/* RevisionPanel — Step 9 Feature 3 revision tracker UI.
 *
 * Compact panel for the Project Dashboard:
 *   • List of all revisions for the project (newest first)
 *   • "Save current as revision" button → prompts for a label
 *   • For each revision: View / Restore / select-for-diff
 *   • Diff modal shows KPI delta + entity-level changes
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  History, Save, RotateCcw, GitCompare,
  CheckCircle2, X, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function Btn({ children, onClick, disabled, color = "#0f172a",
                  testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick} disabled={disabled}
      style={{
        background: color, color: "#cbd5e1",
        border: "1px solid #334155", borderRadius: 4,
        fontSize: 11, padding: "4px 8px",
        display: "inline-flex", alignItems: "center", gap: 4,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}>
      {children}
    </button>
  );
}

export default function RevisionPanel({ projectId }) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [diffSel, setDiffSel] = useState([]);    // up to 2 ids
  const [diffData, setDiffData] = useState(null);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true); setError(null);
    try {
      const r = await axios.get(
        `${API}/api/projects/${projectId}/revisions`,
        { withCredentials: true });
      setRevisions(r.data?.revisions || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  const handleSave = async () => {
    const label = window.prompt(
      "Save as revision — enter a label",
      `Snapshot ${new Date().toLocaleString()}`);
    if (!label) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/projects/${projectId}/revisions`,
        { label }, { withCredentials: true });
      reload();
    } catch (e) {
      alert(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  const handleRestore = async (rev) => {
    if (!window.confirm(
        `Restore "${rev.label}" (#${rev.revision_number}) as the `
        + "current state? This overwrites the live project data, but `"
        + "the current state is recoverable via 'Save as revision'.")) {
      return;
    }
    try {
      await axios.post(
        `${API}/api/projects/${projectId}/revisions/${rev.id}/restore`,
        {}, { withCredentials: true });
      alert(`Restored "${rev.label}" — reload the page to see the new state.`);
      reload();
    } catch (e) {
      alert(`Restore failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const toggleDiff = (rev) => {
    setDiffSel((cur) => {
      if (cur.includes(rev.id)) {
        return cur.filter((id) => id !== rev.id);
      }
      const next = [...cur, rev.id];
      if (next.length > 2) next.shift();
      return next;
    });
  };

  const runDiff = async () => {
    if (diffSel.length !== 2) return;
    try {
      const r = await axios.post(
        `${API}/api/projects/${projectId}/revisions/diff`,
        { rev_a_id: diffSel[0], rev_b_id: diffSel[1] },
        { withCredentials: true });
      setDiffData(r.data);
    } catch (e) {
      alert(`Diff failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div data-testid="revision-panel"
        style={{
          background: "#0b1220", border: "1px solid #1e293b",
          borderRadius: 8, padding: 16,
        }}>
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between", marginBottom: 12,
      }}>
        <h3 style={{ margin: 0, fontSize: 14, color: "#cbd5e1",
                        display: "flex", alignItems: "center", gap: 6 }}>
          <History size={14}/> Revisions ({revisions.length})
        </h3>
        <div style={{ display: "flex", gap: 6 }}>
          {diffSel.length === 2 && (
            <Btn onClick={runDiff} color="#1e40af"
                testid="revision-run-diff">
              <GitCompare size={11}/> Diff selected
            </Btn>
          )}
          <Btn onClick={handleSave} disabled={saving}
              color="#7c3aed" testid="revision-save-current">
            {saving ? <Loader2 size={11}
                          style={{
                            animation: "spin 1s linear infinite",
                          }}/>
                     : <Save size={11}/>} Save current
          </Btn>
        </div>
      </div>

      {error && (
        <div style={{ color: "#fca5a5", fontSize: 11,
                        marginBottom: 8 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading…</div>
      ) : revisions.length === 0 ? (
        <div style={{ color: "#64748b", fontSize: 12, padding: 12,
                        textAlign: "center" }}>
          No revisions yet. Click <strong>Save current</strong> to
          snapshot this state as a named revision.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column",
                        gap: 6, maxHeight: 320, overflowY: "auto" }}>
          {revisions.map((r) => {
            const sel = diffSel.includes(r.id);
            return (
              <div key={r.id}
                  data-testid={`revision-row-${r.id}`}
                  style={{
                    padding: 8, borderRadius: 4,
                    background: sel ? "#1e40af44" : "#0f172a",
                    border: `1px solid ${sel ? "#3b82f6" : "#1e293b"}`,
                    display: "flex", alignItems: "center", gap: 8,
                  }}>
                <input type="checkbox" checked={sel}
                    onChange={() => toggleDiff(r)}
                    data-testid={`revision-checkbox-${r.id}`}
                    style={{ cursor: "pointer" }}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600,
                                  color: "#e2e8f0" }}>
                    #{r.revision_number} · {r.label}
                  </div>
                  <div style={{ fontSize: 10, color: "#64748b",
                                  marginTop: 2 }}>
                    {new Date(r.created_at).toLocaleString()}
                    {" · "}{r.created_by?.email || "—"}
                    {" · BUA "}{r.kpis?.bua_sqm} m²
                    {" · FSI "}{r.kpis?.fsi}
                  </div>
                </div>
                <Btn onClick={() => handleRestore(r)}
                    testid={`revision-restore-${r.id}`}>
                  <RotateCcw size={10}/> Restore
                </Btn>
              </div>
            );
          })}
        </div>
      )}

      {diffData && (
        <div data-testid="revision-diff-modal"
            onClick={() => setDiffData(null)}
            style={{
              position: "fixed", inset: 0,
              background: "#000000cc", zIndex: 999,
              display: "flex", alignItems: "center",
              justifyContent: "center", padding: 24,
            }}>
          <div onClick={(e) => e.stopPropagation()}
              style={{
                background: "#0b1220", border: "1px solid #1e293b",
                borderRadius: 8, padding: 20, color: "#e2e8f0",
                width: "min(720px, 100%)", maxHeight: "80vh",
                overflowY: "auto",
              }}>
            <div style={{ display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>
                <GitCompare size={14}
                    style={{ verticalAlign: -2, marginRight: 6 }}/>
                Revision diff
              </h3>
              <button onClick={() => setDiffData(null)}
                  data-testid="revision-diff-close"
                  style={{
                    background: "transparent", border: "none",
                    color: "#94a3b8", cursor: "pointer",
                  }}><X size={16}/></button>
            </div>
            <div style={{ display: "flex", gap: 16,
                            fontSize: 11, marginBottom: 12 }}>
              <div style={{ flex: 1, padding: 8,
                              background: "#0f172a", borderRadius: 4 }}>
                <div style={{ color: "#94a3b8" }}>A</div>
                <div style={{ fontWeight: 600 }}>
                  #{diffData.a.revision_number} · {diffData.a.label}
                </div>
              </div>
              <div style={{ flex: 1, padding: 8,
                              background: "#0f172a", borderRadius: 4 }}>
                <div style={{ color: "#94a3b8" }}>B</div>
                <div style={{ fontWeight: 600 }}>
                  #{diffData.b.revision_number} · {diffData.b.label}
                </div>
              </div>
            </div>
            <h4 style={{ margin: "12px 0 4px",
                            fontSize: 12, color: "#94a3b8" }}>
              KPI changes ({diffData.kpi_diff?.length || 0})
            </h4>
            {diffData.kpi_diff?.length ? (
              <table style={{ width: "100%", fontSize: 11,
                                borderCollapse: "collapse" }}>
                <thead><tr style={{ color: "#94a3b8" }}>
                  <th style={{ textAlign: "left", padding: 4 }}>Field</th>
                  <th style={{ textAlign: "right", padding: 4 }}>Before</th>
                  <th style={{ textAlign: "right", padding: 4 }}>After</th>
                  <th style={{ textAlign: "right", padding: 4 }}>Δ</th>
                </tr></thead>
                <tbody>
                  {diffData.kpi_diff.map((r, i) => (
                    <tr key={i} style={{
                      borderTop: "1px solid #1e293b",
                    }}>
                      <td style={{ padding: 4 }}>{r.field}</td>
                      <td style={{ padding: 4, textAlign: "right",
                                      color: "#94a3b8" }}>
                        {String(r.before ?? "—")}
                      </td>
                      <td style={{ padding: 4, textAlign: "right" }}>
                        {String(r.after ?? "—")}
                      </td>
                      <td style={{
                        padding: 4, textAlign: "right",
                        color: typeof r.delta === "number"
                          ? (r.delta > 0 ? "#fbbf24" : "#67e8f9")
                          : "#94a3b8",
                      }}>{r.delta ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: "#10b981", fontSize: 11,
                              padding: 8 }}>
                <CheckCircle2 size={12}
                    style={{ verticalAlign: -1, marginRight: 4 }}/>
                No KPI changes between these revisions.
              </div>
            )}
            <h4 style={{ margin: "12px 0 4px",
                            fontSize: 12, color: "#94a3b8" }}>
              Entity changes ({diffData.entity_diff?.length || 0})
            </h4>
            {diffData.entity_diff?.length ? (
              <ul style={{ fontSize: 11, paddingLeft: 18 }}>
                {diffData.entity_diff.map((r, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    <code style={{ color: "#67e8f9" }}>
                      {r.entity_id}
                    </code>
                    {" — "}{r.change}
                    {r.fields && (
                      <span style={{ color: "#94a3b8",
                                        marginLeft: 6 }}>
                        ({Object.keys(r.fields).join(", ")})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: "#10b981", fontSize: 11,
                              padding: 8 }}>
                <CheckCircle2 size={12}
                    style={{ verticalAlign: -1, marginRight: 4 }}/>
                No entity changes between these revisions.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
